import React, { useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { adminService } from '../services/adminService';
import { useCustomers } from '../hooks/useCustomers';
import { useCustomerActions } from '../hooks/useCustomerActions';
import CustomerTable from './customers/CustomerTable';
import CustomerDetailsModal from './customers/CustomerDetailsModal';
import ApprovalModal from './customers/ApprovalModal';
import RejectModal from './customers/RejectModal';
import DocumentViewerModal from './customers/DocumentViewerModal';
import ActionHistoryModal from './customers/ActionHistoryModal';

const CustomerManagement = () => {
  // 1. Data & Filtering Hook
  const {
    customers,
    filteredCustomers,
    loading,
    error,
    loadCustomers,
    updateLocalCustomer,
    // Filter props
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    provisionFilter,
    setProvisionFilter,
    sortConfig,
    setSortConfig
  } = useCustomers();

  // 2. Actions Hook (Approvals, Cancellations, Status Updates)
  // Pass setters to allow hook to update local state optimistically
  const {
    approvalModalOpen,
    setApprovalModalOpen,
    customerToApprove,
    approveCustomer,
    approvalLoading,
    approvalNotes,
    setApprovalNotes,
    serviceActivationDate,
    setServiceActivationDate,
    vocusServiceId,
    setVocusServiceId,
    avcId,
    setAvcId,

    rejectModalOpen,
    setRejectModalOpen,
    customerToReject,
    rejectCustomer,
    rejectionLoading,
    rejectionNotes,
    setRejectionNotes,
    rejectionSendEmail,
    setRejectionSendEmail,

    updateStatus
  } = useCustomerActions(customers, null, updateLocalCustomer);

  // 3. Local UI State for Viewers (Details, Docs, History)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [viewerDoc, setViewerDoc] = useState(null);
  const [historyCustomer, setHistoryCustomer] = useState(null);

  // Per-customer B2B Provision modal removed — provisioning now lives in the standalone
  // "Provision Service" admin tab (ProvisionTool.jsx), which can load a signup customer and
  // provision for real. The provision status badge in the customer table is kept for
  // visibility; the markProvisioned recorder stays in CustomerDetailsModal.

  // --- Handlers ---

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setDetailsModalOpen(true);
  };

  // Documents are fetched on demand as a 5-minute presigned S3 URL. They used
  // to arrive inline as base64 on every customer in the list payload, so the
  // whole document set was in the browser before anyone clicked anything.
  // These take the customer (not the document) because the lookup is by UID.
  const fetchDocUrl = async (customer) => {
    if (!customer?.UID) {
      alert('This customer record has no UID, so the document cannot be looked up.');
      return null;
    }
    try {
      return await adminService.getProofOfAddressUrl(customer.UID);
    } catch (e) {
      console.error('Proof-of-address fetch failed:', e);
      // Documents expire 90 days after upload, so "gone" is a normal outcome
      // for an older account rather than an error worth alarming about.
      alert(e.message?.includes('No proof-of-address')
        ? 'No document is on file. Documents are deleted 90 days after upload.'
        : `Could not open the document: ${e.message}`);
      return null;
    }
  };

  const handleViewDocument = async (customer) => {
    const doc = await fetchDocUrl(customer);
    if (doc) setViewerDoc({ url: doc.url, name: doc.name, size: doc.size });
  };

  const handleDownloadDocument = async (customer) => {
    const doc = await fetchDocUrl(customer);
    if (!doc) return;
    // Presigned URLs are same-origin-agnostic; open in a new tab so the
    // browser handles the content-disposition rather than us building a blob.
    window.open(doc.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-6 text-[#ebdbb2]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#ebdbb2]">Customer Management</h1>
          <p className="text-[#bdae93]">Manage all Peaknet customers and their documents</p>
        </div>
        <button
          onClick={loadCustomers}
          className="px-4 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg hover:bg-[#fe8019] transition-colors"
        >
          Refresh Data
        </button>
      </div>

      {/* Filters Toolbar */}
      <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-[#7c6f64]" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] placeholder-[#7c6f64] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="on_hold">On Hold</option>
            <option value="rejected">Rejected</option>
          </select>

          {/* Provision Filter (B2B FIBRE Create state) */}
          <select
            value={provisionFilter}
            onChange={(e) => setProvisionFilter(e.target.value)}
            className="px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
          >
            <option value="all">All Provision</option>
            <option value="none">Not Provisioned</option>
            <option value="pending">Provision Pending</option>
            <option value="active">Provision Active</option>
            <option value="inactive">Provision Failed</option>
            <option value="withdrawn">Withdrawn</option>
            <option value="rejected">Provision Rejected</option>
          </select>

          {/* Sort */}
          <select
            value={sortConfig.key}
            onChange={(e) => setSortConfig(prev => ({ ...prev, key: e.target.value }))}
            className="px-4 py-2 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
          >
            <option value="createdAt">Created Date</option>
            <option value="fullName">Name</option>
            <option value="email">Email</option>
          </select>
        </div>
      </div>

      {/* Main Content */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#fabd2f]"></div>
        </div>
      ) : error ? (
        <div className="bg-red-900/20 border border-red-500/50 p-6 rounded-lg text-center">
          <h3 className="text-xl text-red-500 mb-2">Error Loading Data</h3>
          <p className="text-red-300">{error}</p>
          <button onClick={loadCustomers} className="mt-4 text-[#fabd2f] underline">Try Again</button>
        </div>
      ) : (
        <CustomerTable
          customers={filteredCustomers}
          onViewCustomer={handleViewCustomer}
          onUpdateStatus={updateStatus}
          onViewDocument={handleViewDocument}
          onDownloadDocument={handleDownloadDocument}
        />
      )}

      {/* --- Modals section --- */}

      {/* 1. Customer Details (Edit/View) */}
      {detailsModalOpen && selectedCustomer && (
        <CustomerDetailsModal
          customer={customers.find(c => c.UID === (selectedCustomer.UID || selectedCustomer.accountNumber)) || selectedCustomer}
          onClose={() => setDetailsModalOpen(false)}
          updateLocalCustomer={updateLocalCustomer}
          onViewDocument={handleViewDocument}
          onDownloadDocument={handleDownloadDocument}
          onOpenActionHistory={() => setHistoryCustomer(selectedCustomer)}
          onResetPassword={async () => {
            if (confirm('Send password reset email to this customer?')) {
              try {
                // We need to import adminService here or use a hook. 
                // Since I haven't imported it, I will use window.adminService or import it.
                const { adminService } = await import('../services/adminService');
                const res = await adminService.sendPasswordReset(selectedCustomer.email);
                if (res.success) alert('Password reset email sent.');
                else alert('Failed to send: ' + res.error);
              } catch (e) { alert(e.message); }
            }
          }}
          onApprove={() => updateStatus(selectedCustomer, 'approved')}
          onReject={() => updateStatus(selectedCustomer, 'rejected')}
        />
      )}

      {/* 2. Approval Logic */}
      {approvalModalOpen && customerToApprove && (
        <ApprovalModal
          customer={customerToApprove}
          onClose={() => setApprovalModalOpen(false)}
          onApprove={approveCustomer}
          approvalLoading={approvalLoading}
          approvalNotes={approvalNotes}
          setApprovalNotes={setApprovalNotes}
          serviceActivationDate={serviceActivationDate}
          setServiceActivationDate={setServiceActivationDate}
          vocusServiceId={vocusServiceId}
          setVocusServiceId={setVocusServiceId}
          avcId={avcId}
          setAvcId={setAvcId}
        />
      )}

      {/* 2b. Reject Modal — replaces the native prompt() from before */}
      {rejectModalOpen && customerToReject && (
        <RejectModal
          customer={customerToReject}
          onClose={() => setRejectModalOpen(false)}
          onReject={rejectCustomer}
          rejectionLoading={rejectionLoading}
          rejectionNotes={rejectionNotes}
          setRejectionNotes={setRejectionNotes}
          sendEmail={rejectionSendEmail}
          setSendEmail={setRejectionSendEmail}
        />
      )}

      {/* 3. Cancellation flow lives under the Cancel Requests tab (lambda-customer-cancel-request).
          Removed the inline CancellationModal here — it called the old half-implemented
          handleCancelService which left Cognito, invoices, and the Vocus service orphaned. */}

      {/* 4. Document Viewer */}
      {viewerDoc && (
        <DocumentViewerModal
          document={viewerDoc}
          onClose={() => setViewerDoc(null)}
          // The modal already holds a live presigned URL — no second lookup.
          onDownload={(d) => window.open(d.url, '_blank', 'noopener,noreferrer')}
        />
      )}

      {/* 5. Action History Viewer */}
      {historyCustomer && (
        <ActionHistoryModal
          customer={historyCustomer}
          onClose={() => setHistoryCustomer(null)}
        />
      )}

    </div>
  );
};

export default CustomerManagement;